import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth, TwoFactorRequiredError } from '@repletoryreach/sdk-auth';
import { isApiError } from '@repletoryreach/sdk-api';
import { Stack, Button, Group, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, PasswordInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../../i18n/I18nProvider';
import { TwoFactorForm } from './TwoFactorForm';

export function LoginForm() {
  const t = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Where to land after sign-in. RequireAuth stashes the attempted location in
   * `state.from`, so an emailed deep link survives the login bounce instead of
   * dumping the user on the dashboard
   * (_UI_improvements/tenant/_app-shell.md §4 item 3).
   */
  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const returnTo = from?.pathname ? `${from.pathname}${from.search ?? ''}` : '/';

  /**
   * The API answers a wrong password with a BODYLESS 401, so the SDK falls back
   * to `HTTP 401` (sdk-api/src/_core/errors.ts:58) and the form used to render
   * that status code straight at the user. Map the codes we actually get onto
   * catalogued copy that says what to do next.
   */
  function messageFor(err: unknown): string {
    if (isApiError(err)) {
      if (err.code === 'WRONG_DOMAIN') return t('auth.login.wrongDomain');
      if (err.status === 429) return t('auth.login.tooMany');
      if (err.status === 401 || err.status === 400) return t('auth.login.errors.badCredentials');
      if (err.status >= 500) return t('auth.login.offline');
    }
    if (err instanceof Error && /network|fetch|timeout/i.test(err.message)) {
      return t('auth.login.errors.unavailable');
    }
    return t('auth.login.failed');
  }

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [stage, setStage] = useState<'credentials' | 'twofa'>('credentials');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      if (err instanceof TwoFactorRequiredError) {
        setStage('twofa');
      } else {
        setError(messageFor(err));
      }
    } finally {
      setBusy(false);
    }
  }

  if (stage === 'twofa') {
    return <TwoFactorForm onDone={() => navigate(returnTo, { replace: true })} onBack={() => { setStage('credentials'); setError(null); }} />;
  }

  return (
    <form onSubmit={submitCredentials}>
      <Stack gap="md">
        <TextInput
          label={t('common.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          required
          autoComplete="email"
          autoFocus
          placeholder={t('auth.login.emailPlaceholder')}
          leftSection={<MailIcon />}
        />
        {/* The recovery link belongs to the password field, so it sits tight
            beneath it rather than floating equidistant between the field and
            the submit button. */}
        <Stack gap="xs">
          <PasswordInput
            label={t('auth.common.password')}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            required
            autoComplete="current-password"
            placeholder={t('auth.login.passwordPlaceholder')}
            leftSection={<LockIcon />}
          />
          {/* Was a 12px muted <Link>: the smallest thing on the page for the
              primary recovery path (auth-login.md §4 item 10). */}
          <Group justify="flex-end">
            <Link to="/auth/forgot-password" className="rr-auth__link">
              {t('auth.login.forgotPassword')}
            </Link>
          </Group>
        </Stack>
        {error && <Alert color="error" role="alert">{error}</Alert>}
        <Button type="submit" loading={busy} fullWidth>{t('action.signIn')}</Button>
      </Stack>
    </form>
  );
}

function FieldIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}
function MailIcon() { return <FieldIcon><rect x="1.75" y="3.25" width="12.5" height="9.5" rx="1.5" /><path d="M2 4.5l6 4 6-4" /></FieldIcon>; }
function LockIcon() { return <FieldIcon><rect x="3" y="7" width="10" height="6.5" rx="1.5" /><path d="M5.5 7V5a2.5 2.5 0 015 0v2" /></FieldIcon>; }
